import turf from 'turf'
export default {
    TILE_SIZE: 256,

    /**
     * Convert a pixel X coordinate to the tile X number.
     *
     * @param pixelX
     *            the pixel X coordinate that should be converted.
     * @param zoom
     *            the zoom level at which the coordinate should be converted.
     * @return the tile X number.
     */
    pixelXToTileX(pixelX, zoom) {
        return Math.floor(Math.min(Math.max(pixelX / this.TILE_SIZE, 0), Math.pow(2, zoom) - 1));
    },

    /**
     * Converts a pixel Y coordinate to the tile Y number.
     *
     * @param pixelY
     *            the pixel Y coordinate that should be converted.
     * @param zoom
     *            the zoom level at which the coordinate should be converted.
     * @return the tile Y number.
     */
    pixelYToTileY(pixelY, zoom) {
        return Math.floor(Math.min(Math.max(pixelY / this.TILE_SIZE, 0), Math.pow(2, zoom) - 1));
    },

    /**
     * Convert a latitude coordinate (in degrees) to a pixel Y coordinate at a
     * certain zoom level.
     *
     * @param latitude
     *            the latitude coordinate that should be converted.
     * @param zoom
     *            the zoom level at which the coordinate should be converted.
     * @return the pixel Y coordinate of the latitude value.
     */
    latitudeToPixelY(latitude, zoom) {
        let sinLatitude = Math.sin(latitude * Math.PI / 180);
        return (0.5 - Math.log((1 + sinLatitude) / (1 - sinLatitude)) / (4 * Math.PI)) * (this.TILE_SIZE << zoom);
    },

    /**
     * Convert a latitude coordinate (in degrees) to a tile Y number at a
     * certain zoom level.
     *
     * @param latitude
     *            the latitude coordinate that should be converted.
     * @param zoom
     *            the zoom level at which the coordinate should be converted.
     * @return the tile Y number of the latitude value.
     */
    latitudeToTileY(latitude, zoom) {
        let py = this.latitudeToPixelY(latitude, zoom);
        return this.pixelYToTileY(py, zoom);
    },

    /**
     * Convert a longitude coordinate (in degrees) to a pixel X coordinate at a
     * certain zoom level.
     *
     * @param longitude
     *            the longitude coordinate that should be converted.
     * @param zoom
     *            the zoom level at which the coordinate should be converted.
     * @return the pixel X coordinate of the longitude value.
     */
    longitudeToPixelX(longitude, zoom) {
        return (longitude + 180) / 360 * (this.TILE_SIZE << zoom);
    },

    /**
     * Convert a longitude coordinate (in degrees) to the tile X number at a
     * certain zoom level.
     *
     * @param longitude
     *            the longitude coordinate that should be converted.
     * @param zoom
     *            the zoom level at which the coordinate should be converted.
     * @return the tile X number of the longitude value.
     */
    longitudeToTileX(longitude, zoom) {
        let px = this.longitudeToPixelX(longitude, zoom);
        return this.pixelXToTileX(px, zoom);
    },

    /**
     * Convert a pixel X coordinate at a certain zoom level to a longitude
     * coordinate.
     *
     * @param pixelX
     *            the pixel X coordinate that should be converted.
     * @param zoom
     *            the zoom level at which the coordinate should be converted.
     * @return the longitude value of the pixel X coordinate.
     */
    pixelXToLongitude(pixelX, zoom) {
        return 360 * ((pixelX / (this.TILE_SIZE << zoom)) - 0.5);
    },


    /**
     * Convert a tile X number to a pixel X coordinate.
     *
     * @param tileX
     *            the tile X number that should be converted
     * @return the pixel X coordinate
     */
    tileXToPixelX(tileX) {
        return tileX * this.TILE_SIZE;
    },

    /**
     * Convert a tile Y number to a pixel Y coordinate.
     *
     * @param tileY
     *            the tile Y number that should be converted
     * @return the pixel Y coordinate
     */
    tileYToPixelY(tileY) {
        return tileY * this.TILE_SIZE;
    },

    /**
     * Convert a pixel Y coordinate at a certain zoom level to a latitude
     * coordinate.
     *
     * @param pixelY
     *            the pixel Y coordinate that should be converted.
     * @param zoom
     *            the zoom level at which the coordinate should be converted.
     * @return the latitude value of the pixel Y coordinate.
     */
    pixelYToLatitude(pixelY, zoom) {
        let y = 0.5 - (pixelY / (this.TILE_SIZE << zoom));
        return 90 - 360 * Math.atan(Math.exp(-y * 2 * Math.PI)) / Math.PI;
    },

    /**
     * Convert a tile X number at a certain zoom level to a longitude
     * coordinate.
     *
     * @param tileX
     *            the tile X number that should be converted.
     * @param zoom
     *            the zoom level at which the number should be converted.
     * @return the longitude value of the tile X number.
     */
    tileXToLongitude(tileX, zoom) {
        return this.pixelXToLongitude(tileX * this.TILE_SIZE, zoom);
    },

    /**
     * Convert a tile Y number at a certain zoom level to a latitude coordinate.
     *
     * @param tileY
     *            the tile Y number that should be converted.
     * @param zoom
     *            the zoom level at which the number should be converted.
     * @return the latitude value of the tile Y number.
     */
    tileYToLatitude(tileY, zoom) {
        return this.pixelYToLatitude(tileY * this.TILE_SIZE, zoom);
    },

    //根据tile的x,y,z计算tile对应的经纬度范围
    xyz2Bound(x, y, z) {
        /*let lngLeft = this.tileXToLongitude(x, z) - 0.00105;
        let latUp = this.tileYToLatitude(y, z) + 0.00105;
        let lngRight = this.tileXToLongitude(x + 1, z) + 0.00105;
        let latDown = this.tileYToLatitude(y + 1, z) - 0.00105;*/

        let lngLeft = this.tileXToLongitude(x, z);
        let latUp = this.tileYToLatitude(y, z);
        let lngRight = this.tileXToLongitude(x + 1, z);
        let latDown = this.tileYToLatitude(y + 1, z);
        return [lngLeft, latDown, lngRight, latUp];
    },

    //根据范围和级别，计算瓦片数量
    calTileCounts(feature, zoom)
    {
        let box = turf.bbox(feature);
        let startx = this.longitudeToTileX(box[0], zoom);
        let endx = this.longitudeToTileX(box[2], zoom);
        let starty = this.latitudeToTileY(box[3], zoom);
        let endy = this.latitudeToTileY(box[1], zoom);

        return Math.abs((endx-startx)*(endy-starty));
    },

    unionFeatures(features) {
        if (features.length == 0) return null;
        let p1 =  features[0];
        for (let i = 1; i < features.length; i++) {
            let p2 = features[i] ;
            p1 = turf.union(p1, p2);
        }
        return p1;
    },

    getBounds(feature){
        let bounds=turf.bbox(feature);
        let boundsStr= String(bounds);
        return boundsStr;
    },

    getCenter(feature){
        let center=turf.center(feature)
        let centerStr=String(center.geometry.coordinates);
        return centerStr;
    },

    //计算范围内的某个级别的x-y范围
/*
    calTileExtent_zxy(feature, zoom)
    {
        let box = turf.bbox(feature);
        let startx = this.longitudeToTileX(box[0], zoom);
        let endx = this.longitudeToTileX(box[2], zoom);
        let starty = this.latitudeToTileY(box[3], zoom);
        let endy = this.latitudeToTileY(box[1], zoom);
        return {"z":zoom,"sx":startx,"ex":endx,"sy":starty,"ey":endy};
    },
*/

    //计算范围内的某个级别的x-y范围
    calTileExtent_zxy(feature, fromZoom, toZoom)
    {
        let tilezxy = [];
        let box = turf.bbox(feature);
        for(let i=fromZoom;i<=toZoom;i++)
        {
            let zoom = i;
            let startx = this.longitudeToTileX(box[0], zoom);
            let endx = this.longitudeToTileX(box[2], zoom);
            let starty = this.latitudeToTileY(box[3], zoom);
            let endy = this.latitudeToTileY(box[1], zoom);
            tilezxy.push({"z":zoom,"sx":startx,"ex":endx,"sy":starty,"ey":endy});
        }

        return tilezxy;
    },

    //计算某个级别下，经纬度所在的z x y
    getzxy(zoom, lat, lont) {
        let x = this.longitudeToTileX(lont, zoom);
        let y = this.latitudeToTileY(lat, zoom);
        let bound = this.xyz2Bound(x, y, zoom);
        return zoom + "-" + x + "-" + y + '  [' + bound + ' ]';
    },

    //计算与区域相交有效的瓦片
    calTileExtent(feature, minZoom, maxZoom)
    {
        let zoom = minZoom;
        let tilezxys = [];//存储下级别的z-x-y范围
        let bounds = [];//存储bound范围
        let tlBounds = [];//存储tile多边形
        let bzPoints = [];//存储tile中心点
        let box = turf.bbox(feature);
        let startx = this.longitudeToTileX(box[0], zoom);
        let endx = this.longitudeToTileX(box[2], zoom);
        let starty = this.latitudeToTileY(box[3], zoom);
        let endy = this.latitudeToTileY(box[1], zoom);
        for(let x=startx;x<=endx;x++)
        {
            for (let y=starty;y<=endy;y++) {
                let bound = this.xyz2Bound(x, y, zoom);
                var poly = turf.bboxPolygon(bound);
                var res = turf.intersect(feature, poly);
                if (res) {//相交则进行处理
                    bounds.push(bound);
                    //计算这个格网的下级瓦片zxy范围
                    //let tilezxy = this.calTileExtent_zxy(poly, minZoom, maxZoom);
                    tilezxys.push({ "x":x,
                        "y":y,
                        "z":zoom,
                        "bounds":String(bound),
                        "deleteZooms":[]});

                    let bz = x + "_" + y + "_" + zoom;
                    let prop =  {
                        "x": x,
                        "y": y,
                        "z": zoom,
                        "bz": bz
                    };
                    //构造tile多边形要素
                    poly.properties = prop;
                    tlBounds.push(poly);

                    //构造tile中心点标注
                    let cent = turf.center(poly);
                    cent.properties = prop;
                    bzPoints.push(cent);
                }
            }
        }
        let bdFeatures = {
           "type": "FeatureCollection",
           "features":tlBounds
         };
        let bzFeatures = {
            "type": "FeatureCollection",
            "features":bzPoints
        };
        return [bdFeatures, bzFeatures, bounds, tilezxys];
    },
}



// WEBPACK FOOTER //
// ./src/assets/js/global_tool/sys_tool/TileUtil.js